import {
  PenLine, FileText, Box, Sofa, ClipboardCheck, BarChart3,
  // Step-cue icons — matched semantically to each step's description
  MessageSquare, Compass, Lightbulb, Ruler, Stamp, HardHat,
  Search, PenTool, Send, Bell, MessageCircle, Flag,
  ShieldCheck, Target, ListChecks, GitBranch, FileCheck,
  Eye, SlidersHorizontal, Wrench, LifeBuoy,
  Coffee, Palette, BookOpen, PackageCheck,
  Map, Calculator, FileBarChart,
} from "lucide-react";

/**
 * Canonical services list — used by Home (interactive showcase) and Studio
 * (deep-dive anchors). Each entry:
 *  - code:    URL anchor (`#architecture`, `#da`, ...) and testid slug
 *  - label:   short 2–3 char pill (DA, CDC, ID, DVS, ARCH, FEA)
 *  - icon:    lucide icon component (whole service)
 *  - title:   full display name
 *  - blurb:   1-line summary (used on Home compact + Studio jump-index)
 *  - detail:  short intro paragraph shown above the numbered steps
 *  - steps:   [{ n, title, body, cue }] — cue is a lucide icon that
 *             illustrates the specific step; drives the visual tile shown
 *             beside each step in the Home showcase carousel.
 */
export const SERVICES = [
  {
    code: "architecture",
    label: "ARCH",
    icon: PenLine,
    title: "Architectural Design & Documentation",
    blurb: "From first sketch to construction-ready drawings, we guide your project through every stage of design.",
    detail: "From first sketch to construction-ready drawings, we guide your project through every stage of design.",
    steps: [
      { n: 1, title: "Initial Consultation & Brief", body: "We meet to understand your goals, lifestyle, budget and timeline, and review your site. This becomes a clear design brief that guides every decision that follows.", cue: MessageSquare },
      { n: 2, title: "Site Analysis & Planning Review", body: "We assess the site's constraints and opportunities (orientation, slope, easements, setbacks, and the LEP, DCP and SEPP controls that apply) so the design is grounded in what can actually be approved.", cue: Compass },
      { n: 3, title: "Concept Design", body: "We develop initial layouts and massing options that respond to your brief and the site. We refine these with you until the direction feels right.", cue: Lightbulb },
      { n: 4, title: "Design Development", body: "The chosen concept is resolved in detail: floor plans, elevations, sections, materials and finishes. We coordinate with consultants such as engineers, surveyors and BASIX assessors where needed.", cue: Ruler },
      { n: 5, title: "Approval Documentation", body: "We prepare the full drawing set required for your approval pathway, whether that's a Development Application or a Complying Development Certificate.", cue: Stamp },
      { n: 6, title: "Construction Documentation", body: "Once approved, we produce detailed Construction Certificate drawings that give your builder the clarity they need to price and build accurately.", cue: HardHat },
    ],
  },
  {
    code: "da",
    label: "DA",
    icon: FileText,
    title: "Development Applications (DA)",
    blurb: "For projects that need council consent, we manage the application from preparation through to determination.",
    detail: "For projects that need council consent, we manage the application from preparation through to determination.",
    steps: [
      { n: 1, title: "Pre-Lodgement Review", body: "We review the applicable planning controls and identify any issues early. Where it's useful, we arrange a pre-lodgement meeting with council.", cue: Search },
      { n: 2, title: "Design & Documentation", body: "We prepare architectural plans, a Statement of Environmental Effects, and coordinate the supporting reports your project requires.", cue: PenTool },
      { n: 3, title: "Lodgement via the NSW Planning Portal", body: "We compile and lodge the complete application, so it's accepted without delays caused by missing information.", cue: Send },
      { n: 4, title: "Notification & Assessment", body: "Council notifies neighbours and assesses the proposal. We monitor progress and act as your point of contact throughout.", cue: Bell },
      { n: 5, title: "Responding to Requests for Information", body: "If council requests amendments or further information, we prepare a clear, timely response to keep your application moving.", cue: MessageCircle },
      { n: 6, title: "Determination & Next Steps", body: "Once your DA is determined, we explain the conditions of consent and help you move to the Construction Certificate stage.", cue: Flag },
    ],
  },
  {
    code: "cdc",
    label: "CDC",
    icon: ClipboardCheck,
    title: "Complying Development Certificates (CDC)",
    blurb: "A faster approval pathway for projects that meet the NSW Codes SEPP standards.",
    detail: "A faster approval pathway for projects that meet the NSW Codes SEPP standards.",
    steps: [
      { n: 1, title: "Eligibility Assessment", body: "We check whether your site and proposal qualify for complying development, reviewing zoning, lot size, and any exclusions such as heritage or environmental constraints.", cue: ShieldCheck },
      { n: 2, title: "Compliance-Led Design", body: "We design your project to meet the relevant development standards from the outset, avoiding redesigns later.", cue: Target },
      { n: 3, title: "Compliance Assessment", body: "We prepare a detailed assessment demonstrating how the proposal satisfies each applicable standard.", cue: ListChecks },
      { n: 4, title: "Documentation & Consultant Coordination", body: "We prepare the full drawing package and coordinate supporting documents such as BASIX, engineering and survey.", cue: GitBranch },
      { n: 5, title: "Submission to Certifier", body: "We submit the package to your chosen certifier, private or council, and respond to any queries until the CDC is issued.", cue: FileCheck },
    ],
  },
  {
    code: "dvs",
    label: "DVS",
    icon: Box,
    title: "Design Verification Statements (DVS)",
    blurb: "For commercial and industrial complying development, we verify your design against the Business Zone Design Guide.",
    detail: "For commercial and industrial complying development, we verify your design against the Business Zone Design Guide.",
    steps: [
      { n: 1, title: "Project Review", body: "We review your proposal, site, and the design criteria that apply under the Codes SEPP and the Business Zone Design Guide.", cue: Eye },
      { n: 2, title: "Design Assessment", body: "We assess the design against each relevant criterion, including built form, landscaping, access and streetscape.", cue: SlidersHorizontal },
      { n: 3, title: "Design Recommendations", body: "Where a design falls short, we recommend practical changes so it can meet the criteria without compromising function.", cue: Wrench },
      { n: 4, title: "Statement Preparation", body: "We prepare a clear, well-referenced Design Verification Statement to accompany your CDC application.", cue: FileText },
      { n: 5, title: "Certifier Support", body: "We respond to any certifier queries to help your application reach approval smoothly.", cue: LifeBuoy },
    ],
  },
  {
    code: "id",
    label: "ID",
    icon: Sofa,
    title: "Interior Design",
    blurb: "Considered interiors that reflect how you live, with materials and details resolved to the last finish.",
    detail: "Considered interiors that reflect how you live, with materials and details resolved to the last finish.",
    steps: [
      { n: 1, title: "Discovery & Brief", body: "We explore your style, how you use each space, and your budget, gathering references to shape a clear direction.", cue: Coffee },
      { n: 2, title: "Concept & Mood", body: "We present a design concept with mood boards, a material palette and spatial ideas for your feedback.", cue: Palette },
      { n: 3, title: "Design Development", body: "We resolve layouts, joinery, lighting, and material and finish selections in detail.", cue: Ruler },
      { n: 4, title: "Documentation", body: "We produce interior drawings, joinery details and a finishes schedule that your builder and trades can work from with confidence.", cue: BookOpen },
      { n: 5, title: "Selections & Coordination", body: "We help finalise selections and coordinate with suppliers and trades so the finished space matches the design intent.", cue: PackageCheck },
    ],
  },
  {
    code: "feasibility",
    label: "FEA",
    icon: BarChart3,
    title: "Development Feasibility & Cost Planning",
    blurb: "Know what your site can deliver, and what it will cost, before you commit.",
    detail: "Know what your site can deliver, and what it will cost, before you commit.",
    steps: [
      { n: 1, title: "Site & Planning Analysis", body: "We review zoning, development controls and site constraints to establish what can realistically be built.", cue: Map },
      { n: 2, title: "Development Options", body: "We test yield scenarios, such as dwelling numbers, configurations and GFA, to find the strongest outcome for the site.", cue: GitBranch },
      { n: 3, title: "Cost Planning", body: "We prepare cost estimates for construction and associated development costs.", cue: Calculator },
      { n: 4, title: "Feasibility Reporting", body: "We deliver a clear feasibility report suitable for decision-making and for supporting finance applications with lenders.", cue: FileBarChart },
    ],
  },
];
